import { ApolloClient, ApolloLink, InMemoryCache, split } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { getFrontendConfigValue, waitFor } from '@arnold/common';
import { captureException, captureMessage } from '@sentry/browser';
import { createUploadLink } from 'apollo-upload-client';
import { createClient } from 'graphql-ws';
import packageJson from '../package.json';
import auth, { ACCESS_TOKEN_PARAM } from './lib/auth';
import ee from './lib/eventEmitter';

const errorLink = onError(({ graphQLErrors, networkError, forward, operation }) => {
  if (
    graphQLErrors &&
    // @ts-ignore
    graphQLErrors.some((error) => !error.info?.supressErrorModal)
  ) {
    ee.emitModalGraphQlError(graphQLErrors[0]);
    graphQLErrors.forEach((error) => {
      // this is in fact NOT an error object despite ts type so use captureMessage instead
      captureMessage(`GraphQL error: ${error.message}`, 'error');
      // tslint:disable-next-line no-console
      console.error('[GraphQL error]:', error);
    });
  }
  if (graphQLErrors && graphQLErrors.some((error) => error.name === 'AuthenticationRequiredError')) {
    auth.logout();
  }

  if (networkError) {
    // tslint:disable-next-line no-console
    captureException(networkError);
    console.error('[Network error]:', networkError);
  }
});

const fetchWithAuth = async (uri: string, options: RequestInit) => {
  // In case of password change or Teamio respondents link, we need to pass the access token from the URL
  const accessToken = getAccessTokenFromUrl();
  if (accessToken != null) {
    (options.headers as Record<string, string>).Authorization = `Bearer ${accessToken}`;
  }
  return fetch(uri, options);
};

const fetchWithRetry = async (uri: string, options: RequestInit): Promise<Response> => {
  let modalShowed = false;
  const response = await fetchWithAuth(uri, options).catch(async (error) => {
    if (error instanceof TypeError) {
      // Error like this can happen if the network is down
      // If API would just return 400, it won't be caught here
      console.error('Error fetching:', error);
      const isMut = isMutation(options);
      if (isMut) {
        modalShowed = true;
        await new Promise((resolve) => {
          ee.emitModalNetworkError(true, isMut, () => resolve(true));
        });
        return fetchWithRetry(uri, options);
      } else {
        // If it's not a mutation, we need to wait for the network to be online and automatically retry
        ee.emitModalNetworkError(true, isMut);
        modalShowed = true;
        await waitFor(() => navigator.onLine, 60 * 1000, 1000, undefined, true);
        return fetchWithRetry(uri, options);
      }
    }
    throw error;
  });
  if (modalShowed) {
    ee.emitModalNetworkError(false);
    modalShowed = false;
  }
  return response;
};

const isMutation = (options: RequestInit) => {
  try {
    const body = JSON.parse(options.body as string);
    return body.query?.startsWith('mutation');
  } catch (e) {
    return false;
  }
};

/**
 * AccessToken is normally in cookies, but in case of password change or Teamio respondents link, we need to pass the access token from the URL
 */
const getAccessTokenFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const tokenFromTeamio = urlParams.get(ACCESS_TOKEN_PARAM);
  if (tokenFromTeamio) {
    return tokenFromTeamio;
  }
  // if url includes changePassword/:token return token
  const path = window.location.pathname;
  const pathParts = path.split('/');
  // iterate over path parts and return the token
  for (let i = 0; i < pathParts.length; i++) {
    if (pathParts[i] === 'changePassword') {
      return pathParts[i + 1];
    }
  }
};

const GRAPHQL_URI = getFrontendConfigValue('API_URL') + '/graphql';

const uploadLink = createUploadLink({
  uri: GRAPHQL_URI,
  fetch: fetchWithRetry,
  credentials: 'include',
  fetchOptions: {
    mode: 'cors',
  },
});

const getCorrectProtocol = () => (window.location.protocol === 'http:' ? 'ws' : 'wss');

const getWsUri = (path: string) => `${getCorrectProtocol()}://${getFrontendConfigValue('API_HOST')}/${path}`;

const wsLink = new GraphQLWsLink(
  createClient({
    url: getWsUri('graphql'),
  }),
);

const requestLink = split(
  // split based on operation type
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
);

const client = new ApolloClient({
  name: 'self-care',
  version: packageJson.version,
  cache: new InMemoryCache(),
  link: ApolloLink.from([requestLink, errorLink, uploadLink]),
});

export default client;
